import jsPDF from 'jspdf';
import { generateReceiptHeader, generateReceiptFooter, getCollegeInfo, fetchReceiptTerms } from './receiptHeaderUtils';
import { ensureRupeeFont, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';

// Utility to generate fee receipt PDF
export const generateFeeReceipt = async (paymentData, collegeInfo = null) => {
  const doc = new jsPDF();
  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;

  // Get college info if not provided - with warning for tenant isolation
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
    console.warn('generateFeeReceipt: collegeInfo not passed, using localStorage fallback. This may cause tenant isolation issues.');
  }

  // Generate Header with College Logo, Name, and Address
  let yPos = 15;
  
  const collegeName = collegeInfo.college_name || collegeInfo.name || 'Institution';
  const collegeAddress = collegeInfo.address || '';
  const collegeCity = collegeInfo.city || '';
  const collegeState = collegeInfo.state || '';
  const collegePincode = collegeInfo.pincode || collegeInfo.pin_code || '';
  const collegePhone = collegeInfo.phone || '';
  const collegeEmail = collegeInfo.email || '';

  // College Name - Bold and prominent
  doc.setFontSize(18);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 51, 102);
  doc.text(collegeName.toUpperCase(), pageWidth / 2, yPos, { align: 'center' });
  yPos += 7;

  // Full Address
  doc.setFontSize(9);
  doc.setFont(resolvedFont, 'normal');
  doc.setTextColor(60, 60, 60);

  if (collegeAddress) {
    doc.text(collegeAddress, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
  }

  const cityStatePincode = [collegeCity, collegeState, collegePincode].filter(Boolean).join(', ');
  if (cityStatePincode) {
    doc.text(cityStatePincode, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
  }

  // Phone and Email
  const contactInfo = [];
  if (collegePhone) contactInfo.push(`Ph: ${collegePhone}`);
  if (collegeEmail) contactInfo.push(`Email: ${collegeEmail}`);
  
  if (contactInfo.length > 0) {
    doc.setFontSize(8);
    doc.text(contactInfo.join('  |  '), pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
  }

  // Decorative line
  doc.setDrawColor(0, 51, 102);
  doc.setLineWidth(1);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 2;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Receipt Title
  doc.setFontSize(14);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('FEE RECEIPT', pageWidth / 2, yPos, { align: 'center' });
  yPos += 3;

  const titleWidth = doc.getTextWidth('FEE RECEIPT');
  doc.setLineWidth(0.5);
  doc.line((pageWidth - titleWidth) / 2 - 5, yPos, (pageWidth + titleWidth) / 2 + 5, yPos);
  yPos += 10;

  // Receipt Details
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  doc.setTextColor(0, 0, 0);

  // Receipt Number and Date
  doc.text(`Receipt No:`, margin, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(`${paymentData.receipt_no || paymentData.id?.substring(0, 8).toUpperCase() || 'N/A'}`, margin + 28, yPos);
  
  doc.setFont(resolvedFont, 'normal');
  doc.text(`Date:`, pageWidth - margin - 45, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(`${formatDate(paymentData.payment_date || paymentData.created_at || new Date())}`, pageWidth - margin, yPos, { align: 'right' });

  yPos += 12;

  // Student Information Section
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, yPos - 5, pageWidth - 2 * margin, 8, 'F');
  doc.text('STUDENT INFORMATION', margin + 2, yPos);
  yPos += 10;

  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  const studentName = paymentData.student_name || 
                      `${paymentData.student?.first_name || ''} ${paymentData.student?.last_name || ''}`.trim() ||
                      'N/A';
  const admissionNo = paymentData.admission_number || 
                      paymentData.student?.admission_no || 
                      paymentData.student?.admission_number ||
                      paymentData.student_id ||
                      'N/A';
  const course = paymentData.course || paymentData.student?.course || 'N/A';
  const semester = paymentData.semester || paymentData.student?.semester || 'N/A';

  // Two column layout for student info
  doc.text(`Student Name:`, margin, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(studentName, margin + 32, yPos);
  
  doc.setFont(resolvedFont, 'normal');
  doc.text(`Student ID:`, pageWidth / 2 + 10, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(admissionNo, pageWidth / 2 + 35, yPos);
  
  yPos += 7;
  doc.setFont(resolvedFont, 'normal');
  doc.text(`Course:`, margin, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(course, margin + 32, yPos);
  
  doc.setFont(resolvedFont, 'normal');
  doc.text(`Semester:`, pageWidth / 2 + 10, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(semester, pageWidth / 2 + 35, yPos);

  yPos += 12;

  // Payment Details Section
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, yPos - 5, pageWidth - 2 * margin, 8, 'F');
  doc.text('PAYMENT DETAILS', margin + 2, yPos);
  yPos += 10;

  // Table Headers
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Description', margin + 2, yPos);
  doc.text(`Amount (${CURRENCY})`, pageWidth - margin - 2, yPos, { align: 'right' });
  
  yPos += 2;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Fee Items
  doc.setFont(resolvedFont, 'normal');
  const feeType = paymentData.fee_type || 'Course Fees';
  const amount = paymentData.amount || paymentData.amount_paid || 0;
  
  doc.text(feeType, margin + 2, yPos);
  doc.text(amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), pageWidth - margin - 2, yPos, { align: 'right' });
  
  yPos += 10;

  // Total
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;
  
  doc.setFont(resolvedFont, 'bold');
  doc.setFontSize(11);
  doc.text('Total Amount Paid:', margin + 2, yPos);
  doc.text(`${CURRENCY} ${formatINR(amount, 2)}`, pageWidth - margin - 2, yPos, { align: 'right' });

  yPos += 12;

  // Payment Method Details
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  const paymentMode = paymentData.payment_mode || paymentData.payment_method || 'N/A';
  const referenceNo = paymentData.reference_number || paymentData.reference_no || '';
  
  doc.text(`Payment Mode:`, margin, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(paymentMode.charAt(0).toUpperCase() + paymentMode.slice(1), margin + 32, yPos);
  
  if (referenceNo && paymentMode.toLowerCase() !== 'cash') {
    doc.setFont(resolvedFont, 'normal');
    doc.text(`Reference No:`, pageWidth / 2 + 10, yPos);
    doc.setFont(resolvedFont, 'bold');
    doc.text(referenceNo, pageWidth / 2 + 42, yPos);
  }

  // Remarks
  if (paymentData.remarks) {
    yPos += 7;
    doc.setFont(resolvedFont, 'normal');
    doc.text(`Remarks:`, margin, yPos);
    doc.setFont(resolvedFont, 'italic');
    const remarksLines = doc.splitTextToSize(paymentData.remarks, pageWidth - 2 * margin - 35);
    doc.text(remarksLines, margin + 32, yPos);
  }

  // Generate Footer with tenant-specific Installment terms
  const terms = await fetchReceiptTerms('installment');
  generateReceiptFooter(doc, collegeInfo, terms);

  // Save the PDF
  const fileName = `FeeReceipt_${paymentData.receipt_no || paymentData.id?.substring(0, 8) || 'Payment'}_${new Date().getTime()}.pdf`;
  doc.save(fileName);
  
  return doc;
};

// Utility to generate multiple receipts at once (for reports)
export const generateBulkReceipts = (payments, collegeInfo = null) => {
  if (!payments || payments.length === 0) {
    alert('No payments to generate receipts for');
    return;
  }

  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
  }

  payments.forEach((payment, index) => {
    setTimeout(() => {
      generateFeeReceipt(payment, collegeInfo);
    }, index * 500);
  });
};
